// pt-BR is the source of truth. en.ts must mirror every key — enforced by
// tests/i18n.test.ts, which fails on a key present in one dictionary and not the
// other. Same shape as the portal's apps/web/src/i18n/pt-BR.ts so both repos
// read identically.
//
// Conventions:
//   • flat dotted keys, no nesting — `menu.narrator`, not `{ menu: { narrator } }`
//   • `{name}` placeholders are substituted by getT's second argument
//   • keys are grouped by prefix only for readability; nothing parses the prefix

export const ptBR = {
  // ── Common ────────────────────────────────────────────────────────────────
  'common.close': 'Fechar',
  'common.cancel': 'Cancelar',
  'common.back': 'Voltar',
  'common.more': 'Mais',
  'common.moreAria': 'Mais opções',
  'common.on': 'Ligado',
  'common.off': 'Desligado',

  // ── Menu groups (section headings in the overflow sheet) ──────────────────
  'menu.group.navegar': 'Navegar',
  'menu.group.ver': 'Visualizar',
  'menu.group.compartilhar': 'Compartilhar',
  'menu.group.acessibilidade': 'Acessibilidade',
  'menu.group.ferramentas': 'Ferramentas',

  // ── Menu entries ──────────────────────────────────────────────────────────
  // `.label` is the short form shown in the rail tooltip and the sheet row.
  // `.aria` is the full sentence for assistive tech. `.title` is the hover hint.
  'menu.floorplan.label': 'Planta',
  'menu.floorplan.aria': 'Mostrar ou ocultar a planta baixa',
  'menu.floorplan.title': 'Planta baixa (F)',

  // Same button, same panel — used when the tour carries no floor-plan geometry
  // and the panel falls back to a list of rooms. Calling that list "Planta"
  // promised a drawing the visitor never got. Resolved by MenuItem.labelsFor.
  'menu.rooms.label': 'Ambientes',
  'menu.rooms.aria': 'Mostrar ou ocultar a lista de ambientes',
  'menu.rooms.title': 'Ambientes (F)',

  'menu.dollhouse.label': 'Maquete',
  'menu.dollhouse.aria': 'Mostrar ou ocultar a maquete 3D',
  'menu.dollhouse.title': 'Maquete 3D (M)',

  'menu.variants.label': 'Variações',
  'menu.variants.aria': 'Escolher a variação de design',
  'menu.variants.count': 'Variações ({current}/{total})',

  'menu.sun.label': 'Hora do dia',
  'menu.sun.aria': 'Ajustar a hora do dia',
  'menu.sun.title': 'Hora do dia (S)',

  'menu.share.label': 'Link',
  'menu.share.aria': 'Copiar link compartilhável desta vista',
  'menu.share.title': 'Copiar link com preview (L)',

  'menu.qr.label': 'QR code',
  'menu.qr.aria': 'Mostrar QR code da vista atual',
  'menu.qr.title': 'QR code da vista atual (Q)',

  'menu.whatsapp.label': 'WhatsApp',
  'menu.whatsapp.aria': 'Compartilhar pelo WhatsApp',
  'menu.whatsapp.title': 'Enviar este apartamento pelo WhatsApp',

  'menu.chat.label': 'Assistente',
  'menu.chat.aria': 'Conversar com o assistente',
  'menu.chat.title': 'Pergunte sobre o ambiente, materiais, navegação',

  'menu.gallery.label': 'Galeria',
  'menu.gallery.aria': 'Variantes que você explorou',
  'menu.gallery.title': 'Galeria de variantes geradas por IA',

  'menu.savedSession.label': 'Salvar',
  'menu.savedSession.aria': 'Salvar minha sessão',
  'menu.savedSession.title': 'Salvar sessão — volte depois exatamente onde parou',

  'menu.narrator.label': 'Narração',
  'menu.narrator.aria': 'Ligar o tour narrado',
  'menu.narrator.ariaOff': 'Desligar o tour narrado',
  'menu.narrator.title': 'Tour narrado',

  'menu.autotour.label': 'Tour',
  'menu.autotour.aria': 'Iniciar o tour cinemático',
  'menu.autotour.title': 'Tour cinemático — a câmera percorre cada ambiente',
  'menu.autotourStop.label': 'Parar tour',
  'menu.autotourStop.aria': 'Parar o tour cinemático',

  'menu.audio.label': 'Som',
  'menu.audio.aria': 'Ativar o áudio ambiente',
  'menu.audio.ariaOff': 'Desativar o áudio ambiente',

  'menu.voice.label': 'Voz',
  'menu.voice.aria': 'Comando de voz',
  'menu.voice.title': 'Comando de voz · ex: "ir para a cozinha", "antes e depois"',

  'menu.tilt.label': 'Inclinar',
  'menu.tilt.aria': 'Explorar inclinando o aparelho',
  'menu.tilt.title': 'Incline o aparelho para olhar ao redor',

  'menu.vr.label': 'Entrar VR',
  'menu.vr.labelExit': 'Sair VR',
  'menu.vr.aria': 'Entrar no modo realidade virtual',

  'menu.language.label': 'Idioma',
  'menu.language.aria': 'Escolher o idioma',

  // Operator-only (behind ?operator=1)
  'menu.measure.label': 'Medir',
  'menu.measure.aria': 'Medir distâncias',
  'menu.note.label': 'Nota',
  'menu.note.aria': 'Deixar uma nota neste ambiente',
  'menu.report.label': 'Relatório',
  'menu.report.aria': 'Baixar o relatório do tour em PDF',
  'menu.copresence.label': 'Convidar',
  'menu.copresence.aria': 'Convidar alguém para um tour compartilhado',
  'menu.follow.label': 'Acompanhar',
  'menu.follow.aria': 'Acompanhar a vista do outro participante',
  'menu.dashboard.label': 'Insights',
  'menu.dashboard.aria': 'Abrir os insights da sessão',
  'menu.debug.label': 'Depurar',
  'menu.debug.aria': 'Mostrar a leitura de yaw e pitch',

  // ── Bottom bar ────────────────────────────────────────────────────────────
  'bottombar.beforeAfter': 'Antes / Depois',
  'bottombar.compare': 'Comparar',
  'bottombar.compareTitle': 'Capture um snapshot e arraste para comparar com a variante Antes',
  'bottombar.variantsAria': 'Variações de design',

  // ── Topbar ────────────────────────────────────────────────────────────────
  'topbar.eyebrow': 'Ambiente',

  // ── Language sheet ────────────────────────────────────────────────────────
  'language.pt-BR': 'Português (BR)',
  'language.en': 'English',
} as const;

export type Dict = Record<keyof typeof ptBR, string>;
export type DictKey = keyof typeof ptBR;
